import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MoonLoader } from "react-spinners";
import { useTranslate } from "../../../../hooks/LanguageProvider";

type VehicleMapItem = {
  id_vehicule?: number;
  immatriculation_vehicule?: string;
  TIMESTAMP?: string;
};

type TransmissionGapChartProps = {
  title?: string;
  thresholdHours?: number;
  refreshSeconds?: number;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const ALGERIA_OFFSET_HOURS = 1;

function mapTimeZone(timestamp: string) {
  const date = new Date(timestamp);
  date.setHours(date.getHours() + ALGERIA_OFFSET_HOURS);
  return date.toLocaleString();
}

function hoursSince(timestamp?: string) {
  if (!timestamp) return null;
  const parsed = new Date(mapTimeZone(timestamp)).getTime();
  if (!Number.isFinite(parsed)) return null;
  const diff = (Date.now() - parsed) / 3600000;
  return diff >= 0 ? diff : 0;
}

const TransmissionGapChart: React.FC<TransmissionGapChartProps> = ({
  title = "Absence transmission",
  thresholdHours = 1,
  refreshSeconds = 60,
}) => {
  const { translate } = useTranslate();
  const [vehicles, setVehicles] = useState<VehicleMapItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const safeThreshold = Number(thresholdHours) > 0 ? Number(thresholdHours) : 1;

  const fetchVehicles = async () => {
    const userId = localStorage.getItem("userid");
    if (!backendUrl || !userId) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${backendUrl}/api/map/find/${userId}`, { mode: "cors" });
      if (!response.ok) throw new Error("Erreur API map");
      const data = await response.json();
      setVehicles(Array.isArray(data) ? data : []);
    } catch (error: any) {
      setError(error.message || "Erreur API map");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    const intervalMs = Math.max(15, Number(refreshSeconds) || 60) * 1000;
    const interval = window.setInterval(fetchVehicles, intervalMs);
    return () => window.clearInterval(interval);
  }, [thresholdHours, refreshSeconds]);

  const stats = useMemo(() => {
    const withAge = vehicles
      .map((vehicle) => ({ vehicle, age: hoursSince(vehicle.TIMESTAMP) }))
      .filter((item): item is { vehicle: VehicleMapItem; age: number } => item.age !== null);
    const absent = withAge.filter(({ age }) => age > safeThreshold);
    const ok = vehicles.length - absent.length;
    const critical = absent.filter(({ age }) => age > 24).length;
    const latestAbsent = absent
      .sort((a, b) => b.age - a.age)
      .slice(0, 3);
    return { absent, ok, critical, latestAbsent };
  }, [vehicles, safeThreshold]);

  const options: Highcharts.Options = {
    chart: {
      type: "pie",
      height: 245,
      backgroundColor: "transparent",
      spacing: [0, 0, 0, 0],
    },
    credits: { enabled: false },
    title: { text: undefined },
    tooltip: { pointFormat: "<b>{point.y}</b> vehicules" },
    plotOptions: {
      pie: {
        innerSize: "68%",
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          distance: 8,
          format: "{point.name}: {point.y}",
        },
      },
    },
    series: [{
      type: "pie",
      name: "Transmission",
      data: [
        { name: translate("OK"), y: stats.ok, color: "#18b26b" },
        { name: translate("Sans transmission"), y: stats.absent.length, color: "#dc2626" },
      ],
    }],
  };

  return (
    <div className="dash2-chart-widget dash2-transmission-widget">
      <div className="dash2-chart-header">
        <div>
          <span className="dash2-chart-kicker">&gt; {safeThreshold}h - Algerie UTC+{ALGERIA_OFFSET_HOURS}</span>
          <h3>{title}</h3>
        </div>
        <button type="button" className="dash2-chart-refresh" onClick={fetchVehicles} title={translate("Refresh")}>
          <i className="las la-sync" />
        </button>
      </div>

      <div className="dash2-chart-metrics">
        <span><strong>{vehicles.length}</strong>Total</span>
        <span><strong>{stats.absent.length}</strong>{translate("Sans transmission")}</span>
        <span><strong>{stats.critical}</strong>&gt; 24h</span>
      </div>

      {loading && !vehicles.length && (
        <div className="dash2-chart-loader">
          <MoonLoader size={34} color="#2563eb" loading={loading} />
        </div>
      )}
      {error && <div className="dash2-chart-empty">{error}</div>}
      {!error && vehicles.length > 0 && <HighchartsReact highcharts={Highcharts} options={options} />}
      {!loading && !error && vehicles.length === 0 && <div className="dash2-chart-empty">Aucune donnee map.</div>}

    </div>
  );
};

export default TransmissionGapChart;
